<script setup>
import {  ref, onBeforeMount, defineProps } from 'vue';
import {useRouter, useRoute} from "vue-router"
import { Api } from '@/service/api';
import draggable from 'vuedraggable'
import PlayCircle from '@/components/UI/PlayCircle.vue';
import LoadingSvg from "@/components/UI/LoadingSvg.vue";
import {datetimeOptions} from "@/service/options";

const router = useRouter();
const route = useRoute();


const connections = ref([])

const props = defineProps({
    workflow_id: String
})


onBeforeMount(async () => {
    connections.value = await Api.getWorkflowConnections(props.workflow_id)
})

async function updateStatus(connection_id) {
    const conn = await Api.getConnectionStatus(props.workflow_id, connection_id)

    for (let i = 0; i < connections.value.length; i++) {
        if (conn.id === connections.value[i].id) {
            connections.value[i].last_run_status = conn.last_run_status
        }
    }

    if (conn.last_run_status === "RUNNING") {
        setTimeout(async function () {
            await updateStatus(connection_id);
        }, 3000)
    } else {
        const apiRequest = await Api.getConnectionStatus(props.workflow_id, connection_id)

        for (let i = 0; i < connections.value.length; i++) {
            if (apiRequest.id === connections.value[i].id) {
                connections.value[i].last_run_status = apiRequest.last_run_status
                connections.value[i].last_run_datetime = apiRequest.last_run_datetime
            }
        }
    }
}

async function startConnection(connection_id) {
    for (let i = 0; i < connections.value.length; i++) {
        if (connections.value[i].id === connection_id) {
            connections.value[i].last_run_status = "RUNNING"
        }
    }
    await Api.startConnection(props.workflow_id, connection_id)
    await updateStatus(connection_id);
}

function redirectToCreateConnection() {
    router.push({name: 'CreateConnection', params: {workflow_id: route.params.workflow_id}})
}

async function updatePosition(){
    for (let i = 0; i < connections.value.length; i++) {
        connections.value[i].order_id = i + 1
    }
    let connections_request = []

    connections.value.forEach((connection) => {
        connections_request.push({
            "connection_id": connection.id,
            "order_id": connection.order_id
        })
    })

    await Api.updateConnectionsOrder(props.workflow_id, connections_request)
}

function getFormattedDatetime(datetime) {
    const date = new Date(datetime)
    return `${date.toLocaleDateString('ru-RU', datetimeOptions)}`
}


</script>

<template>
    <div class="w-full h-[calc(100%-14px)] flex flex-col items-center justify-start overflow-y-auto">
        <draggable
            class="border-t-gray-200 border-t w-full"
            v-model="connections"
            @start="drag=true"
            @end="drag=false"
            item-key="id"
            handle=".handle"
            @change="updatePosition"
        >
            <template #item="{element}">
                <div class="flex items-center h-14 hover:bg-gray-100 border-b border-b-gray-200 w-full">
                    <div class="group">
                        <div class="flex items-center justify-center size-10 select-none group-hover:hidden">
                            <div class="font-bold">
                                {{ element.order_id }}
                            </div>
                        </div>
                        <div class="items-center justify-center size-10 cursor-pointer hidden group-hover:flex handle">
                            <svg viewBox="0 0 24 24" class="" data-role="drag-icon" style="width: 24px; height: 24px;" aria-labelledby="icon_labelledby_21998"><title id="icon_labelledby_21998">Drag to change running order</title><path d="M9,3H11V5H9V3M13,3H15V5H13V3M9,7H11V9H9V7M13,7H15V9H13V7M9,11H11V13H9V11M13,11H15V13H13V11M9,15H11V17H9V15M13,15H15V17H13V15M9,19H11V21H9V19M13,19H15V21H13V19Z" style="fill: rgb(97, 97, 97);"></path></svg>
                        </div>
                    </div>
                    <div class="justify-center flex flex-col">
                        <div class="font-bold text-xs select-none">
                            {{ element.name }}
                        </div>
                        <div class="flex justify-center items-center">
                            <svg class="size-3 fill-gray-600 mr-2" focusable="false" viewBox="0 0 24 24" aria-hidden="true" title="Last run"><path d="M21 10.12h-6.78l2.74-2.82c-2.73-2.7-7.15-2.8-9.88-.1-2.73 2.71-2.73 7.08 0 9.79s7.15 2.71 9.88 0C18.32 15.65 19 14.08 19 12.1h2c0 1.98-.88 4.55-2.64 6.29-3.51 3.48-9.21 3.48-12.72 0-3.5-3.47-3.53-9.11-.02-12.58s9.14-3.47 12.65 0L21 3v7.12zM12.5 8v4.25l3.5 2.08-.72 1.21L11 13V8h1.5z"></path></svg>
                            <div class="text-[10px] font-bold text-gray-600 select-none block whitespace-nowrap text-ellipsis overflow-hidden">
                                {{ getFormattedDatetime(element.last_run_datetime) }}
                            </div>
                        </div>
                    </div>
                    <div class="ml-auto mr-10 flex">
                        <div class="group/run mr-2 flex justify-center items-center">
                            <div class="flex size-10 items-center justify-center"
                                 v-if="element.last_run_status === 'SUCCESS' || element.last_run_status === 'NOT_STARTED'"
                            >
                                <svg viewBox="0 0 24 24" role="presentation" aria-label="Run" class="size-6 " style="color: rgb(0, 168, 50);"><path d="M20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4C12.76,4 13.5,4.11 14.2,4.31L15.77,2.74C14.61,2.26 13.34,2 12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12M7.91,10.08L6.5,11.5L11,16L21,6L19.59,4.58L11,13.17L7.91,10.08Z" style="fill: currentcolor;"></path></svg>
                            </div>
                            <div class="flex size-10 items-center justify-center"
                                 v-if="element.last_run_status === 'ERROR'"
                            >
                                <svg class="size-6 fill-red-500" focusable="false" viewBox="0 0 24 24" aria-hidden="true"><path d="M14.59 8L12 10.59 9.41 8 8 9.41 10.59 12 8 14.59 9.41 16 12 13.41 14.59 16 16 14.59 13.41 12 16 9.41 14.59 8zM12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"></path></svg>
                            </div>
                            <div
                                class="flex size-10 items-center justify-center"
                                v-if="element.last_run_status === 'RUNNING'"
                            >
                                <LoadingSvg class="size-6 animate-spin"/>
                            </div>
                            <div class="flex rounded-full size-10 hover:bg-gray-200 items-center justify-center cursor-pointer"
                                 v-on:click="startConnection(element.id)"
                                 v-if="element.last_run_status !== 'RUNNING'"
                            >
                                <PlayCircle class="size-6"/>
                            </div>
                        </div>
                        <div class="flex size-10 justify-center items-center hover:bg-gray-200 rounded-full cursor-pointer">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
                                <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                            </svg>
                        </div>
                    </div>
                </div>
            </template>
        </draggable>
        <div class="w-full h-14 flex justify-center items-center font-bold mt-5 mb-10">
            <div class="px-3 py-2 bg-blue-500 text-white rounded-md mx-auto select-none hover:bg-blue-600" v-on:click="redirectToCreateConnection">
                Создать соединение
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>